import { useEffect, useRef, useState } from 'react';
import type { Country, Department, Lang } from '@/data/types';
import { useUser } from '@/auth/AuthContext';
import { WORKERS, deptLabel } from '@/data/workers';
import { useAvatar, setAvatar } from '@/hooks/useAvatar';
import { useInstall } from '@/hooks/useInstall';
import { InstallHowTo } from '@/components/InstallApp';
import { AVATAR_STYLES, avatarUrl, initialsOf } from '@/data/avatars';
import { authHeaders } from '@/lib/authHeaders';
import { PersonFace } from '@/lib/peoplePhotos';
import { useGoogleStatus } from '@/hooks/useGoogleCalendar';
import { COUNTRIES } from '@/data';

const T = (lang: Lang, en: string, es: string, fr: string, pt: string) =>
  lang === 'es' ? es : lang === 'fr' ? fr : lang === 'pt' ? pt : en;

/** One of the caller's own sign-ins, as the server records it. */
interface MySession { at: string; city: string; geo: string; agent: string }

/** "hace 2 horas" / "2 hours ago" — the useful form for a last visit. Exact
 *  dates are for things you have to act on; this is recognition. */
function ago(iso: string, lang: Lang): string {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return '';
  const mins = Math.max(0, Math.round((Date.now() - t) / 60000));
  const U = (n: number, en: string, es: string, fr: string, pt: string) =>
    `${n} ${T(lang, en, es, fr, pt)}${n === 1 ? '' : lang === 'fr' ? 's' : 's'}`;
  if (mins < 2) return T(lang, 'just now', 'ahora mismo', 'à l’instant', 'agora mesmo');
  if (mins < 60) return T(lang, `${mins} min ago`, `hace ${mins} min`, `il y a ${mins} min`, `há ${mins} min`);
  const h = Math.round(mins / 60);
  if (h < 24) {
    const w = U(h, 'hour', 'hora', 'heure', 'hora');
    return T(lang, `${w} ago`, `hace ${w}`, `il y a ${w}`, `há ${w}`);
  }
  const d = Math.round(h / 24);
  const w = U(d, 'day', 'día', 'jour', 'dia');
  return T(lang, `${w} ago`, `hace ${w}`, `il y a ${w}`, `há ${w}`);
}

/**
 * The profile panel — who you are here, how you look to the team, and the
 * two facts about your own account worth being able to check: when you last
 * came in, and from where.
 *
 * Everything on it is either read-only truth (name, role, jurisdiction, email
 * — Lexidy owns those) or genuinely yours to change (your picture). It asks
 * for nothing: there is no form, no preferences to fill in, nothing to save.
 */
export function Profile({ lang, onClose, country, dept, isSuper = false }: {
  lang: Lang;
  onClose: () => void;
  country?: Country | null;
  dept?: Department | null;
  isSuper?: boolean;
}) {
  const { user } = useUser();
  const fileRef = useRef<HTMLInputElement>(null);
  const email = user?.email ?? '';
  const avatar = useAvatar(email);
  const app = useInstall();
  const [howTo, setHowTo] = useState(false);
  const [info, setInfo] = useState(false);
  const [picking, setPicking] = useState(false);
  const [sessions, setSessions] = useState<MySession[] | null>(null);
  const me = WORKERS.find((w) => w.email?.toLowerCase() === email.toLowerCase());
  const name = me?.name ?? user?.name ?? (email ? email.split('@')[0] : '—');
  const role = me?.role ?? T(lang, 'Team member', 'Miembro del equipo', 'Membre de l’équipe', 'Membro da equipa');
  /* YOUR TEAM, said with its names (Gerónimo, 29 Oct 2026: «debería decirte tu
     email de Lexidy, tu equipo…»): every country you work, not only the one
     on screen; the department with its second one; and the two accounts the
     portal works through. */
  const myCountries = (me?.countries?.length ? me.countries : me?.country ? [me.country] : country ? [country.code] : [])
    .map((c) => COUNTRIES.find((x) => x.code === c)).filter((c): c is Country => !!c);
  const myDept = me ? deptLabel(me) : dept?.name || '';
  const google = useGoogleStatus().data;

  /* Your own comings and goings. A profile that cannot reach the server still
     opens and simply says nothing about past visits — an empty list, never a
     guess or a spinner that never ends. */
  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const r = await fetch('/api/account/activity', { headers: await authHeaders() });
        const d = r.ok ? ((await r.json()) as { sessions?: MySession[] }) : null;
        if (alive) setSessions(d?.sessions ?? []);
      } catch { if (alive) setSessions([]); }
    })();
    return () => { alive = false; };
  }, []);

  const onPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !email) return;
    if (!file.type.startsWith('image/')) return;
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result !== 'string') return;
      // Downscale to a 256px square (center-crop) so the stored photo stays light.
      const img = new Image();
      img.onload = () => {
        const S = 256;
        const canvas = document.createElement('canvas');
        canvas.width = S; canvas.height = S;
        const ctx = canvas.getContext('2d');
        if (!ctx) { setAvatar(email, reader.result as string); return; }
        const side = Math.min(img.width, img.height);
        ctx.drawImage(img, (img.width - side) / 2, (img.height - side) / 2, side, side, 0, 0, S, S);
        setAvatar(email, canvas.toDataURL('image/jpeg', 0.85));
      };
      img.onerror = () => setAvatar(email, reader.result as string);
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
    e.target.value = '';
    setPicking(false);
  };

  /* The most recent sign-in is THIS one. "Last connection" means the one
     before it — otherwise the panel would proudly report that you are here,
     which you can see for yourself. */
  const last = sessions && sessions.length > 1 ? sessions[1] : null;
  const here = sessions?.[0] ?? null;
  const place = (s: MySession | null) =>
    s ? [s.city, s.geo].filter(Boolean).join(', ') : '';

  return (
    <>
      <div className="svc-modal-backdrop" onClick={onClose}>
        <div className="svc-modal pf-modal" onClick={(e) => e.stopPropagation()}>
          <button className="svc-modal-close" onClick={onClose}>×</button>
          <div className="svc-modal-body">

            {/* ── Who you are ── */}
            <div className="pf-hero">
              {/* Your own picture first; without one, your Google photo, as in
                  the sidebar; without that, your initials. */}
              <div className="pf-av">
                {avatar ? <img src={avatar} alt="" /> : <PersonFace who={{ email }}><span>{initialsOf(name)}</span></PersonFace>}
              </div>
              <div className="pf-who">
                <h2 className="pf-name">{name}</h2>
                <p className="pf-role">{role}</p>
              </div>
            </div>

            {/* ── Your picture ── */}
            <div className="pf-acts">
              <button className="btn btn-sm" onClick={() => fileRef.current?.click()}>
                {avatar
                  ? T(lang, 'Change photo', 'Cambiar foto', 'Changer la photo', 'Mudar foto')
                  : T(lang, 'Upload photo', 'Subir foto', 'Charger une photo', 'Carregar foto')}
              </button>
              <button className={`btn btn-sm ${picking ? 'is-on' : ''}`} onClick={() => setPicking((v) => !v)}>
                {T(lang, 'Choose an avatar', 'Elegir avatar', 'Choisir un avatar', 'Escolher avatar')}
              </button>
              {avatar && (
                <button className="pf-rm" onClick={() => { setAvatar(email, null); setPicking(false); }}>
                  {T(lang, 'Remove', 'Quitar', 'Retirer', 'Remover')}
                </button>
              )}
              <input ref={fileRef} type="file" accept="image/*" hidden onChange={onPick} />
            </div>

            {picking && (
              <>
                <p className="pf-hint">
                  {T(lang,
                    'Your own initials, twelve ways — for anyone who would rather not use a photograph.',
                    'Tus iniciales, de doce formas — para quien prefiere no poner una foto.',
                    'Vos initiales, douze façons — pour qui préfère éviter la photo.',
                    'As suas iniciais, de doze formas — para quem prefere não pôr foto.')}
                </p>
                <div className="pf-avgrid">
                  {AVATAR_STYLES.map((a) => {
                    const url = avatarUrl(a, initialsOf(name));
                    return (
                      <button
                        key={a.id}
                        className={`pf-avpick ${avatar === url ? 'on' : ''}`}
                        title={a.label}
                        aria-label={a.label}
                        onClick={() => { setAvatar(email, url); setPicking(false); }}
                      >
                        <img src={url} alt="" />
                      </button>
                    );
                  })}
                </div>
              </>
            )}

            {/* ── Your team ── */}
            <h3 className="pf-h3">{T(lang, 'Your team', 'Tu equipo', 'Votre équipe', 'A sua equipa')}</h3>
            <dl className="pf-facts">
              <dt>{T(lang, 'Lexidy e-mail', 'Email de Lexidy', 'E-mail Lexidy', 'E-mail da Lexidy')}</dt>
              <dd><a className="pf-mail" href={`mailto:${email}`}>{email}</a></dd>
              {/* The person's own department (roster), not the knowledge base's
                  single «Immigration». */}
              <dt>{T(lang, 'Department', 'Departamento', 'Département', 'Departamento')}</dt>
              <dd>{myDept || '—'}</dd>
              <dt>{myCountries.length > 1 ? T(lang, 'Countries', 'Países', 'Pays', 'Países') : T(lang, 'Country', 'País', 'Pays', 'País')}</dt>
              <dd>{myCountries.length ? myCountries.map((c) => `${c.flag} ${c.name}`).join(' · ') : '—'}</dd>
              <dt>{T(lang, 'In the portal', 'En el portal', 'Dans le portail', 'No portal')}</dt>
              <dd>{isSuper
                ? T(lang, 'Super admin — sees and manages the whole portal', 'Super admin — ve y gestiona todo el portal', 'Super admin — voit et gère tout le portail', 'Super admin — vê e gere todo o portal')
                : T(lang, 'Team member', 'Miembro del equipo', 'Membre de l’équipe', 'Membro da equipa')}</dd>
            </dl>

            {/* ── Your account ── */}
            <h3 className="pf-h3">{T(lang, 'Your account', 'Tu cuenta', 'Votre compte', 'A sua conta')}</h3>
            <dl className="pf-facts">
              <dt>Google</dt>
              <dd className={google?.connected ? '' : 'pf-muted'}>
                {!google
                  ? '…'
                  : google.connected
                    ? `${T(lang, 'Connected as', 'Conectado como', 'Connecté en tant que', 'Ligado como')} ${google.googleEmail}`
                    : T(lang, 'Not connected — the settings wheel connects it', 'Sin conectar — se conecta desde la rueda de ajustes', 'Non connecté — la roue des réglages le connecte', 'Sem ligação — liga-se na roda das definições')}
              </dd>
              <dt>HubSpot</dt>
              <dd className={me?.hubspotOwnerId ? '' : 'pf-muted'}>
                {me?.hubspotOwnerId
                  ? T(lang, 'Linked: your HubSpot tickets and tasks show as yours', 'Vinculado: tus tickets y tareas de HubSpot salen como tuyos', 'Lié : vos tickets et tâches HubSpot apparaissent comme les vôtres', 'Ligado: os seus tickets e tarefas do HubSpot aparecem como seus')
                  : T(lang, 'Not linked yet — an administrator links it', 'Aún sin vincular — lo vincula un administrador', 'Pas encore lié — un administrateur le lie', 'Ainda sem ligação — um administrador liga-o')}
              </dd>
              <dt>{T(lang, 'Last connection', 'Última conexión', 'Dernière connexion', 'Última ligação')}</dt>
              <dd>
                {sessions === null
                  ? '…'
                  : last
                    ? `${ago(last.at, lang)}${place(last) ? ` · ${place(last)}` : ''}`
                    : T(lang, 'This is your first recorded sign-in.', 'Es tu primera entrada registrada.', 'C’est votre première connexion enregistrée.', 'É a sua primeira entrada registada.')}
              </dd>
              <dt>{T(lang, 'Connecting from', 'Te conectas desde', 'Vous vous connectez depuis', 'Liga-se a partir de')}</dt>
              <dd>
                {here && (place(here) || here.agent)
                  ? [place(here), here.agent].filter(Boolean).join(' · ')
                  : T(lang, 'Not known for this session.', 'No consta en esta sesión.', 'Inconnu pour cette session.', 'Não consta nesta sessão.')}
              </dd>
              <dt>{T(lang, 'Managed by', 'Lo gestiona', 'Géré par', 'Gerido por')}</dt>
              <dd className="pf-muted">
                {T(lang,
                  'Lexidy — name, role, jurisdiction and email. Only your picture is yours to change.',
                  'Lexidy — nombre, puesto, jurisdicción y email. Solo la imagen la cambias tú.',
                  'Lexidy — nom, poste, juridiction et e-mail. Seule votre image vous appartient.',
                  'Lexidy — nome, cargo, jurisdição e e-mail. Só a imagem é sua para mudar.')}
              </dd>
            </dl>

            {/* ── The app ──
                One line of state, an action when there is one, and an ⓘ for
                the rest — the detail belongs behind a click. */}
            <h3 className="pf-h3">{T(lang, 'The app', 'La app', 'L’app', 'A app')}</h3>
            <div className="pf-app">
              <div className="pf-app-row">
                <span className={`pf-app-dot ${app.state === 'installed' || app.state === 'probably' ? 'on' : ''}`} />
                <div className="pf-app-txt">
                  <strong>
                    {app.state === 'installed'
                      ? T(lang, 'App installed', 'App instalada', 'App installée', 'App instalada')
                      : app.state === 'declined'
                        ? T(lang, 'Install declined', 'Instalación rechazada', 'Installation refusée', 'Instalação recusada')
                      : app.state === 'probably'
                        ? T(lang, 'Already installed', 'Ya la tienes instalada', 'Déjà installée', 'Já a tem instalada')
                        : app.state === 'unknown'
                          ? T(lang, 'This browser can’t install it', 'Este navegador no puede instalarla', 'Ce navigateur ne peut pas l’installer', 'Este navegador não a consegue instalar')
                          : T(lang, 'App not installed', 'App no instalada', 'App non installée', 'App não instalada')}
                  </strong>
                  <span>
                    {app.state === 'installed'
                      ? (app.standalone
                          ? T(lang, 'You are using it right now.', 'La estás usando ahora mismo.', 'Vous l’utilisez en ce moment.', 'Está a usá-la agora mesmo.')
                          : T(lang, 'It is on this device — open it from your icon.', 'Está en este dispositivo — ábrela desde su icono.', 'Elle est sur cet appareil — ouvrez-la depuis son icône.', 'Está neste dispositivo — abra-a pelo ícone.'))
                      : app.state === 'declined'
                        ? T(lang, 'You said no to the browser dialog. Install it any time from the ⊕ icon at the right of the address bar.', 'Dijiste que no al diálogo del navegador. Puedes instalarla cuando quieras desde el icono ⊕ a la derecha de la barra de direcciones.', 'Vous avez refusé la fenêtre du navigateur. Installez-la depuis l’icône ⊕ à droite de la barre d’adresse.', 'Recusou a janela do navegador. Instale quando quiser a partir do ícone ⊕ à direita da barra de endereço.')
                      : app.state === 'probably'
                        ? T(lang, 'Chrome only offers to install it when you don’t have it. Open it from your apps.', 'Chrome solo ofrece instalarla cuando no la tienes. Ábrela desde tus aplicaciones.', 'Chrome ne propose de l’installer que si vous ne l’avez pas. Ouvrez-la depuis vos applications.', 'O Chrome só oferece instalá-la quando não a tem. Abra-a pelas suas aplicações.')
                        : app.state === 'available'
                          ? T(lang, 'Own window and icon, and notifications with the portal closed.', 'Ventana e icono propios, y notificaciones con el portal cerrado.', 'Fenêtre et icône propres, et notifications portail fermé.', 'Janela e ícone próprios, e notificações com o portal fechado.')
                          : app.state === 'manual'
                            ? T(lang, 'Safari installs it by hand — three taps.', 'Safari la instala a mano — tres toques.', 'Safari l’installe à la main — trois gestes.', 'O Safari instala à mão — três toques.')
                            : T(lang, 'Open the portal in Chrome or Edge to install it in one click.', 'Abre el portal en Chrome o Edge para instalarla de un clic.', 'Ouvrez le portail dans Chrome ou Edge pour l’installer en un clic.', 'Abra o portal no Chrome ou Edge para instalar com um clique.')}
                  </span>
                </div>
                {app.state === 'available' && (
                  <button className="btn btn-primary pf-app-btn" onClick={() => void app.install()}>
                    {T(lang, 'Install', 'Instalar', 'Installer', 'Instalar')}
                  </button>
                )}
                {app.state === 'manual' && (
                  <button className="btn pf-app-btn" onClick={() => setHowTo(true)}>
                    {T(lang, 'How to', 'Cómo', 'Comment', 'Como')}
                  </button>
                )}
                <button
                  className={`pf-app-i ${info ? 'open' : ''}`}
                  onClick={() => setInfo((v) => !v)}
                  aria-expanded={info}
                  aria-label={T(lang, 'More about the app', 'Más sobre la app', 'En savoir plus', 'Mais sobre a app')}
                >
                  i
                </button>
              </div>
              {info && (
                <ul className="pf-app-info">
                  <li>{T(lang, 'Its own window and icon — no browser tabs.', 'Ventana e icono propios — sin pestañas.', 'Fenêtre et icône propres — sans onglets.', 'Janela e ícone próprios — sem separadores.')}</li>
                  <li>{T(lang, 'Notifications reach you with the portal closed.', 'Las notificaciones te llegan con el portal cerrado.', 'Les notifications arrivent portail fermé.', 'As notificações chegam com o portal fechado.')}</li>
                  <li>{T(lang, 'Always up to date — nothing to update by hand.', 'Siempre al día — nada que actualizar a mano.', 'Toujours à jour — rien à mettre à jour.', 'Sempre atualizado — nada para atualizar.')}</li>
                  <li>{T(lang, 'Dismissed the install notice? It comes back next time you open the portal — close the tab and enter again.', '¿Cerraste el aviso de instalación? Vuelve a salir la próxima vez que entres — cierra la pestaña y abre el portal otra vez.', 'Vous avez fermé l’avis ? Il revient à votre prochaine entrée — fermez l’onglet et rouvrez le portail.', 'Fechou o aviso? Volta a aparecer na próxima entrada — feche o separador e abra o portal outra vez.')}</li>
                  <li>{T(lang, 'Chrome not offering it? Look for the ⊕ icon at the right of the address bar.', '¿Chrome no te lo ofrece? Busca el icono ⊕ a la derecha de la barra de direcciones.', 'Chrome ne le propose pas ? Cherchez l’icône ⊕ à droite de la barre d’adresse.', 'O Chrome não oferece? Procure o ícone ⊕ à direita da barra de endereço.')}</li>
                </ul>
              )}
              {app.refused && (
                <p className="pf-app-note">
                  {T(lang,
                    'The browser dialog can only be opened once. Install it from the ⊕ icon at the right of the address bar.',
                    'El diálogo del navegador solo se puede abrir una vez. Instálala desde el icono ⊕ a la derecha de la barra de direcciones.',
                    'La fenêtre du navigateur ne s’ouvre qu’une fois. Installez-la depuis l’icône ⊕ à droite de la barre d’adresse.',
                    'A janela do navegador só abre uma vez. Instale a partir do ícone ⊕ à direita da barra de endereço.')}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
      {howTo && <InstallHowTo lang={lang} onClose={() => setHowTo(false)} />}
    </>
  );
}
